import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { arriveMotion } from '@/src/renderer/lib/motion/arriveMotion'
import { whenCalm } from '@/src/renderer/lib/motion/whenCalm'
import type { SearchHit } from '@/src/renderer/lib/taskJump/openSearchHit'
import { CommandPaletteContent, type CommandPaletteContentProps } from './CommandPaletteContent'
import './CommandPalette.scss'

export interface CommandPaletteDialogProps extends Omit<CommandPaletteContentProps, 'onOpenChange'> {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * **Die Hülle der Suche** (#233): Radix-Dialog mittig im Fenster, 640 px breit, über einem
 * neutralen Schleier (`--search-scrim`, ohne Farbe des Lebensbereichs). Escape, Klick daneben und
 * der Fokus im Feld kommen von Radix; der Inhalt (`CommandPaletteContent`) kennt
 * die Hülle nicht. Der Inhalt wird beim Schließen abgebaut, die nächste Suche beginnt leer.
 *
 * **Aufgehen:** von etwas unten und knapp kleiner herauf (`arriveMotion` als Compositor-Bewegung).
 * Die Suche lädt beim Öffnen ihre Daten; eine Bewegung auf dem Main-Thread ging darin unter. Der
 * ganze `transform` läuft im Compositor weiter, der Start wartet kurz auf Leerlauf (`whenCalm`).
 *
 * @example
 * <CommandPaletteDialog open={open} onOpenChange={setOpen} activeLifeSpace="personal"
 *   lifeSpaces={spaces} onSelect={openHit} onStartFocus={playTask} />
 */
export const CommandPaletteDialog: React.FC<CommandPaletteDialogProps> = ({ open, onOpenChange, onSelect, ...contentProps }) => {
  const { t } = useTranslation()
  const reduced = useReducedMotion() ?? false
  const arrive = useMemo(
    () => arriveMotion({ compositor: true, distance: 10, scale: 0.97, duration: 0.24, delay: 0, exitDuration: 0.14, reduced }),
    [reduced],
  )
  // Bis zum Start bleibt der Anfangszustand stehen. Höchstens 60 ms — ⌘K soll sofort antworten.
  const [calm, setCalm] = useState(false)
  useEffect(() => {
    if (!open) {
      setCalm(false)
      return
    }
    return whenCalm(() => setCalm(true), { maxWait: 60 })
  }, [open])

  // Nach einem Sprung gehört der Fokus der neuen Seite, nicht dem Element, von dem aus man gesucht hat.
  const jumpedRef = useRef(false)
  const handleSelect = (hit: SearchHit, options: { newTab: boolean }) => {
    jumpedRef.current = true
    onSelect(hit, options)
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                className="command-palette-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: reduced ? 0 : 0.15 } }}
                exit={{ opacity: 0, transition: { duration: reduced ? 0 : 0.12 } }}
              />
            </Dialog.Overlay>
            <Dialog.Content
              asChild
              forceMount
              aria-describedby={undefined}
              onOpenAutoFocus={() => { jumpedRef.current = false }}
              onCloseAutoFocus={e => { if (jumpedRef.current) e.preventDefault() }}
            >
              <motion.div className="command-palette-dialog" initial={arrive.initial} animate={calm ? arrive.animate : arrive.initial} exit={arrive.exit}>
                <Dialog.Title className="command-palette-dialog__title">{t('search.label')}</Dialog.Title>
                <CommandPaletteContent {...contentProps} onSelect={handleSelect} onOpenChange={onOpenChange} />
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  )
}
